// animatedGradientBackground.jsx
import { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import './background.css';

const AnimatedGradientBackground = forwardRef((props, ref) => {
    const rootRef = useRef(null);
    const interBubbleRef = useRef(null);
    const curXRef = useRef(0);
    const curYRef = useRef(0);
    const tgXRef = useRef(0);
    const tgYRef = useRef(0);
    const rafIdRef = useRef(null);

    // Ease the bubble towards the pointer; the loop stops once it has caught up
    const move = () => {
        rafIdRef.current = null;
        if (!interBubbleRef.current) return;

        const dx = tgXRef.current - curXRef.current;
        const dy = tgYRef.current - curYRef.current;

        // Only update DOM if the movement is meaningful (> 0.5px)
        if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
            curXRef.current += dx / 50;
            curYRef.current += dy / 50;
            interBubbleRef.current.style.transform =
                `translate(${Math.round(curXRef.current)}px, ${Math.round(curYRef.current)}px)`;
            rafIdRef.current = requestAnimationFrame(move);
        }
    };

    const startLoop = () => {
        if (rafIdRef.current === null) {
            rafIdRef.current = requestAnimationFrame(move);
        }
    };

    useImperativeHandle(ref, () => ({
        handleMouseMove: (event) => {
            if (!event) return;
            tgXRef.current = event.clientX;
            tgYRef.current = event.clientY;
            startLoop();
        },
        // Graph zoom offset; set on this element (not :root) so only the background restyles
        setZoomTransform: (x, y, scale) => {
            const style = rootRef.current?.style;
            if (!style) return;
            style.setProperty('--transform-x', `${x}px`);
            style.setProperty('--transform-y', `${y}px`);
            style.setProperty('--zoom-scale', scale);
        },
        // Pause the slow drift animation while the graph is busy
        setPaused: (paused) => {
            rootRef.current?.classList.toggle('paused', paused);
        }
    }));

    useEffect(() => {
        return () => {
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }
        };
    }, []);

    return (
        <div ref={rootRef} className="gradient-bg" style={{ filter: "grayscale(60%) opacity(30%)" }}>
            <div className="gradients-container">
                <div className="g1"></div>
                <div ref={interBubbleRef} className="interactive"></div>
            </div>
        </div>
    );
});

export default AnimatedGradientBackground;
